import React, { useContext } from 'react'
import { Card, Flex, Box, Icon, ThemeStyleObject } from '../'
import { PropsWithChildren } from 'react'
import { Modal, ModalSize } from './Modal'

export type AdminModalProps = PropsWithChildren<{
    sx?: ThemeStyleObject
    onRequestClose?(event: React.MouseEvent | React.KeyboardEvent): void
    preventClose?: boolean
    maxWidth?: ModalSize
}>

const AdminModalContext = React.createContext<
    Pick<AdminModalProps, 'onRequestClose' | 'preventClose'>
>({})

export const AdminModal = (props: AdminModalProps) => {
    const { onRequestClose, maxWidth = 'md', preventClose = false } = props

    return (
        <Modal
            isOpen={true}
            preventClose={preventClose}
            maxWidth={maxWidth}
            onRequestClose={onRequestClose}
        >
            <Card
                sx={{
                    border: 'none',
                    p: 0,
                    overflow: 'hidden',
                    display: 'flex',
                    flexDirection: 'column',
                    overflowY: 'auto',
                    maxHeight: 'calc(100vh - 64px)',
                    ...props.sx
                }}
            >
                <AdminModalContext.Provider
                    value={{ onRequestClose, preventClose }}
                >
                    {props.children}
                </AdminModalContext.Provider>
            </Card>
        </Modal>
    )
}

export type AdminModalTitleProps = PropsWithChildren<{
    sx?: ThemeStyleObject
    closeable?: boolean
    title: string
    icon?: React.ReactNode
}>

export const AdminModalTitle = ({
    icon,
    title,
    children,
    sx,
    closeable
}: AdminModalTitleProps) => {
    const { onRequestClose, preventClose } = useContext(AdminModalContext)

    return (
        <Flex sx={{ p: 5, flexDirection: 'column', flex: '0 0 auto', ...sx }}>
            <Flex sx={{ flex: 1, justifyContent: 'space-between' }}>
                <Box sx={{ fontSize: 6, fontFamily: 'heading' }}>
                    {icon}
                    {title}
                </Box>
                {closeable && (
                    <Flex
                        sx={{
                            'cursor': preventClose ? 'default' : 'pointer',
                            'alignItems': 'center',
                            '&:hover > svg > path': {
                                color: 'supportText'
                            }
                        }}
                        onClick={ev => {
                            !preventClose &&
                                onRequestClose &&
                                onRequestClose(ev)
                        }}
                    >
                        <Icon.Close
                            sx={{ fontSize: 7 }}
                            color={preventClose ? 'grey10' : 'text'}
                        />
                    </Flex>
                )}
            </Flex>
            {children && <Box sx={{ marginTop: 4 }}>{children}</Box>}
        </Flex>
    )
}

export const AdminModalBody = ({
    sx,
    children
}: PropsWithChildren<{ sx?: ThemeStyleObject }>) => (
    <Box
        sx={{
            backgroundColor: 'cardBackground',
            overflowY: 'auto',
            p: 5,
            flex: '1 1 auto',
            ...sx
        }}
    >
        {children}
    </Box>
)

export type AdminModalActionsProps = PropsWithChildren<{
    sx?: ThemeStyleObject
    align?: 'right' | 'left'
}>

export const AdminModalActions = ({
    sx,
    align = 'right',
    children
}: AdminModalActionsProps) => (
    <Flex
        sx={{
            'backgroundColor': 'cardBackground',
            'p': 5,
            'flex': '0 0 auto',
            'justifyContent': align === 'right' ? 'flex-end' : 'flex-start',
            // spacing between children
            '& > * + *': {
                marginLeft: 2
            },
            ...sx
        }}
    >
        {children}
    </Flex>
)
